import React, { useState, useEffect, useCallback } from 'react';
import { IconClose } from './Icons';
import './AuditLogPanel.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

function authHeaders() {
  const token = localStorage.getItem('symphonate-token');
  return { Authorization: `Bearer ${token}` };
}

// Human-readable labels for the audit actions written by recordAuditEvent.
// Unknown actions fall back to the raw action string so a newly-instrumented
// mutation still renders something sensible without a client change.
const ACTION_LABELS = {
  login: 'Logged in',
  'password.reset.request': 'Requested a password reset',
  'password.reset.complete': 'Completed a password reset',
  'workspace.create': 'Created workspace',
  'invite.create': 'Created an invite',
  'invite.accept': 'Accepted an invite',
  'member.remove': 'Removed a member',
  'member.role_change': 'Changed a member role',
  'billing.tier_change': 'Changed the billing tier',
  'decision.create': 'Logged a decision',
  'room.export': 'Exported a thread',
  'workspace.export': 'Exported the workspace',
  'workspace.decisions.export': 'Exported the decision log',
};

function labelFor(action) {
  return ACTION_LABELS[action] || action;
}

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

// Render the small metadata bag as a compact "key: value" hint, skipping noise.
function describeMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return '';
  const parts = [];
  for (const [k, v] of Object.entries(metadata)) {
    if (v === null || v === undefined || v === '') continue;
    parts.push(`${k}: ${v}`);
  }
  return parts.join(' · ');
}

/**
 * Audit Log — the append-only trail of high-value mutations in a workspace,
 * surfaced to admins+ as a credibility signal for security-conscious buyers.
 * Read-only: the data layer is append-only and there is nothing to edit here.
 */
export default function AuditLogPanel({ workspaceId, workspaceName, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/audit-events`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load audit log');
      const data = await res.json();
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch (e) {
      setError(e.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="audit-backdrop" onClick={onClose} role="presentation">
      <div
        className="audit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Audit log"
      >
        <div className="audit-header">
          <div>
            <h2>Audit Log</h2>
            <p className="audit-subtitle">{workspaceName}</p>
          </div>
          <button className="audit-close" onClick={onClose} aria-label="Close audit log">
            <IconClose size={18} />
          </button>
        </div>

        {loading && <div className="audit-empty">Loading audit log…</div>}
        {error && !loading && <div className="audit-empty audit-error">{error}</div>}

        {!loading && !error && events.length === 0 && (
          <div className="audit-empty">No audit events recorded yet.</div>
        )}

        {!loading && !error && events.length > 0 && (
          <div className="audit-body">
            <p className="audit-hint">
              Append-only record of high-value actions in this workspace. Read-only.
            </p>
            <table className="audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="audit-when">{formatTimestamp(e.createdAt)}</td>
                    <td className="audit-who">{e.actorName || 'System / removed user'}</td>
                    <td className="audit-action">{labelFor(e.action)}</td>
                    <td className="audit-details">{describeMetadata(e.metadata)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
